import type { CreateHjFromCsvRow } from './api'

export type { CreateHjFromCsvRow }

export interface ParseHjCsvSuccess {
  ok: true
  rows: CreateHjFromCsvRow[]
}

export interface ParseHjCsvFailure {
  ok: false
  error: string
}

export type ParseHjCsvResult = ParseHjCsvSuccess | ParseHjCsvFailure

/** Split a CSV line into fields, supporting simple double-quoted values. */
function splitCsvLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      fields.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  fields.push(current.trim())
  return fields
}

/**
 * Parse a CSV string with required headers: email, displayname, roleid (case-insensitive).
 */
export function parseHjCsv(text: string): ParseHjCsvResult {
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = normalized
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)

  if (lines.length < 2) {
    return { ok: false, error: 'CSV must include a header row and at least one data row' }
  }

  const headers = splitCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/["']/g, ''))
  const emailIdx = headers.indexOf('email')
  const displayIdx = headers.indexOf('displayname')
  const roleIdx = headers.indexOf('roleid')

  if (emailIdx === -1 || displayIdx === -1 || roleIdx === -1) {
    return {
      ok: false,
      error: 'CSV header must include columns: email, displayname, roleid',
    }
  }

  const rows: CreateHjFromCsvRow[] = []
  const errors: string[] = []

  for (let i = 1; i < lines.length; i++) {
    const fields = splitCsvLine(lines[i])
    const email = (fields[emailIdx] ?? '').trim()
    const displayname = (fields[displayIdx] ?? '').trim()
    const roleRaw = (fields[roleIdx] ?? '').trim()

    if (!email && !displayname && !roleRaw) continue

    const roleid = Number(roleRaw)
    if (!email || !displayname || !Number.isFinite(roleid) || roleid <= 0) {
      errors.push(`Row ${i + 1}: invalid email, displayname, or roleid`)
      continue
    }

    rows.push({ email, displayname, roleid })
  }

  if (rows.length === 0) {
    return {
      ok: false,
      error: errors.length
        ? `No valid rows. ${errors.slice(0, 3).join('; ')}`
        : 'No valid data rows found',
    }
  }

  if (errors.length > 0) {
    // Still proceed with valid rows; surface count of skipped invalid lines via error if all invalid already handled
    console.warn('CSV parse skipped rows:', errors)
  }

  return { ok: true, rows }
}

export async function parseHjCsvFile(file: File): Promise<ParseHjCsvResult> {
  const text = await file.text()
  return parseHjCsv(text)
}
